import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { postPublicResendEmailVerification } from '../../entities/me/me.api';
import { ApiError } from '../../shared/api/api-error';
import { resolveStaffPostLoginDestination } from '../../shared/access/route-resolver';
import { useAuth } from '../../shared/auth/use-auth';
import { Button } from '../../shared/ui/Button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../shared/ui/Card';
import { Stack } from '../../shared/ui/Stack';
import { ROUTE_PATHS } from '../../app/router/route-paths';

/** Кулдаун повторного письма-верификации; backend rate-limit — не более 10 в час с одного IP. */
const RESEND_COOLDOWN_S = 300;

/**
 * Подтверждение email для staff/тренера/admin (auth-methods-uniform, правило 5/6):
 * вход в приложение только после подтверждения email по ссылке из письма.
 */
export default function ConfirmEmailPage() {
  const { me, apiClient, refreshMe, bootstrapState } = useAuth();
  const navigate = useNavigate();
  const [resendBusy, setResendBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const [cooldown, setCooldown] = useState(0);

  const email = useMemo(() => (me?.email ?? '').trim().toLowerCase(), [me?.email]);

  useEffect(() => {
    if (bootstrapState.kind === 'anonymous') {
      navigate(ROUTE_PATHS.login, { replace: true });
    }
  }, [bootstrapState.kind, navigate]);

  useEffect(() => {
    if (me?.email_verified_at) {
      navigate(resolveStaffPostLoginDestination(undefined, me.capabilities ?? null, me), { replace: true });
    }
  }, [me, navigate]);

  useEffect(() => {
    if (!me || me.email_verified_at) return;
    const id = setInterval(() => {
      void refreshMe().then((fresh) => {
        if (fresh?.email_verified_at) {
          navigate(resolveStaffPostLoginDestination(undefined, fresh.capabilities ?? null, fresh), { replace: true });
        }
      });
    }, 10_000);
    return () => clearInterval(id);
  }, [me, refreshMe, navigate]);

  useEffect(() => {
    if (cooldown <= 0) return;
    const id = setInterval(() => setCooldown((p) => Math.max(0, p - 1)), 1000);
    return () => clearInterval(id);
  }, [cooldown]);

  const onResend = async () => {
    if (!email || resendBusy || cooldown > 0) return;
    setResendBusy(true);
    setNote(null);
    try {
      await postPublicResendEmailVerification(apiClient, email);
      setNote('Письмо отправлено. Проверьте почту.');
      setCooldown(RESEND_COOLDOWN_S);
    } catch (err) {
      if (err instanceof ApiError && err.status === 429) {
        setNote('Слишком частые попытки. Попробуйте позже.');
        setCooldown(RESEND_COOLDOWN_S);
      } else {
        setNote('Не удалось отправить письмо. Попробуйте позже.');
      }
    } finally {
      setResendBusy(false);
    }
  };

  return (
    <div className="staff-system-shell">
      <Card className="staff-system-inner">
        <CardHeader>
          <CardTitle>Подтвердите email</CardTitle>
          <CardDescription>
            {email ? `Мы отправили письмо на ${email}. ` : ''}
            Перейдите по ссылке из письма, чтобы подтвердить адрес и войти в приложение.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Stack gap={4}>
            {note ? (
              <p className="staff-m-0 text-sm text-[hsl(var(--ui-muted-foreground))]" role="status" aria-live="polite">
                {note}
              </p>
            ) : null}
            <Button
              type="button"
              data-testid="staff-resend-verification-email"
              variant="primary"
              disabled={resendBusy || cooldown > 0 || !email}
              onClick={() => void onResend()}
            >
              {resendBusy
                ? 'Отправляем…'
                : cooldown > 0
                  ? `Отправить письмо повторно через ${cooldown} сек`
                  : 'Отправить письмо повторно'}
            </Button>
          </Stack>
        </CardContent>
      </Card>
    </div>
  );
}
